import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import { Timestamp } from "../types";
import { doc, query, getDocs } from "firebase/firestore";
import { PublicOrderFormConfig } from "../types/PublicOrderFormConfig";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

export interface IPublicFormConfig extends PublicOrderFormConfig {
  id: string;
  workspaceId: string;
  allowCustomOrderFlow: boolean;
  allowProductListOrderFlow: boolean;
  language: 'en' | 'no';
  productListFlowTitle: string;
  productListFlowDescription: string;
  productListFlowConfirmationTitle: string;
  productListFlowConfirmationDescription: string;
  productListFlowConfirmationNextSteps: string;
  customOrderFlowTitle: string;
  customOrderFlowDescription: string;
  customOrderFlowConfirmationTitle: string;
  customOrderFlowConfirmationDescription: string;
  customOrderFlowConfirmationNextSteps: string;
  primaryColor?: string;
  primaryColorLight?: string;
  primaryColorDark?: string;
  headerColor?: string;
  logoUrl?: string;
  logoRedirectUrl?: string;
  savedUrl?: string;
  urlSlug?: string;
  useCustomUrlSlug?: boolean;
  clientId?: string;
  clientSecret?: string;
  defaultPickupLocation?: import("../types").ILocation;
  showPricesWithVat?: boolean;
  enableCategoryFilter?: boolean;
  categoryFilters?: { id: string; name: string; sortNr: number }[];
  productOrder?: { id: string; sortNr: number }[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

class PublicFormConfig implements IPublicFormConfig {
  id: string;
  workspaceId: string;
  allowCustomOrderFlow: boolean;
  allowProductListOrderFlow: boolean;
  language: 'en' | 'no';
  productListFlowTitle: string;
  productListFlowDescription: string;
  productListFlowConfirmationTitle: string;
  productListFlowConfirmationDescription: string;
  productListFlowConfirmationNextSteps: string;
  customOrderFlowTitle: string;
  customOrderFlowDescription: string;
  customOrderFlowConfirmationTitle: string;
  customOrderFlowConfirmationDescription: string;
  customOrderFlowConfirmationNextSteps: string;
  primaryColor?: string;
  primaryColorLight?: string;
  primaryColorDark?: string;
  headerColor?: string;
  logoUrl?: string;
  logoRedirectUrl?: string;
  defaultPickupLocation?: import("../types").ILocation;
  savedUrl?: string;
  urlSlug?: string;
  useCustomUrlSlug?: boolean;
  clientId?: string;
  clientSecret?: string;
  showPricesWithVat?: boolean;
  enableCategoryFilter?: boolean;
  categoryFilters?: { id: string; name: string; sortNr: number }[];
  productOrder?: { id: string; sortNr: number }[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;

  static collectionName = "order_form";

  static converter = {
    toFirestore(config: PublicFormConfig) {
      const data = config.data();

      return data;
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      const data = snapshot.data(options) as IPublicFormConfig;
      return new PublicFormConfig({ ...data, id: snapshot.id });
    },
  };

  static default() {
    return new PublicFormConfig({
      id: "",
      workspaceId: "",
      allowCustomOrderFlow: true,
      allowProductListOrderFlow: true,
      language: 'no',
      productListFlowTitle: "Bestill fra produktliste",
      productListFlowDescription: "Velg fra våre standardprodukter med faste priser. Rask og enkel bestilling for vanlige transportbehov.",
      productListFlowConfirmationTitle: "Bestilling mottatt!",
      productListFlowConfirmationDescription: "Vi har mottatt din transportbestilling og vil behandle den så snart som mulig. Du vil bli kontaktet for bekreftelse og tilbud.",
      productListFlowConfirmationNextSteps: "1. Vi gjennomgår din bestilling og beregner pris\n2. Du vil bli kontaktet for bekreftelse av bestillingen\n3. Etter bekreftelse vil transporten bli planlagt og utført",
      customOrderFlowTitle: "Be om tilpasset tilbud",
      customOrderFlowDescription: "Beskriv dine spesifikke transportbehov og få et tilpasset tilbud. Perfekt for unike eller komplekse transportoppdrag.",
      customOrderFlowConfirmationTitle: "Bestilling mottatt!",
      customOrderFlowConfirmationDescription: "Vi har mottatt din transportbestilling og vil behandle den så snart som mulig. Du vil bli kontaktet for bekreftelse og tilbud.",
      customOrderFlowConfirmationNextSteps: "1. Vi gjennomgår din bestilling og beregner pris\n2. Du vil bli kontaktet for bekreftelse av bestillingen\n3. Etter bekreftelse vil transporten bli planlagt og utført",
      primaryColor: "#7214FF", // lgb-blue-300 default
      primaryColorLight: "#F1E8FF", // lgb-blue-100 default  
      primaryColorDark: "#5B0ACD", // lgb-blue-350 default
      headerColor: "#FFFFFF", // White default
      // No logo by default. A non-empty placeholder here would render as a
      // broken image (showing only the alt text) on the public order form.
      logoUrl: "",
      logoRedirectUrl: "",
      defaultPickupLocation: undefined,
      showPricesWithVat: true,
      enableCategoryFilter: false,
      categoryFilters: [],
      productOrder: [],
    });
  }

  static isNew(data: IPublicFormConfig): boolean {
    return data.id.length === 0;
  }

  static createId() {
    return firestore.collection(PublicFormConfig.collectionName).doc().id;
  }

  constructor({
    id,
    workspaceId,
    allowCustomOrderFlow,
    allowProductListOrderFlow,
    language,
    productListFlowTitle,
    productListFlowDescription,
    productListFlowConfirmationTitle,
    productListFlowConfirmationDescription,
    productListFlowConfirmationNextSteps,
    customOrderFlowTitle,
    customOrderFlowDescription,
    customOrderFlowConfirmationTitle,
    customOrderFlowConfirmationDescription,
    customOrderFlowConfirmationNextSteps,
    primaryColor,
    primaryColorLight,
    primaryColorDark,
    headerColor,
    logoUrl,
    logoRedirectUrl,
    defaultPickupLocation,
    savedUrl,
    urlSlug,
    useCustomUrlSlug,
    clientId,
    clientSecret,
    showPricesWithVat,
    enableCategoryFilter,
    categoryFilters,
    productOrder,
    createdAt,
    updatedAt,
  }: IPublicFormConfig) {

    this.id = id;
    this.workspaceId = workspaceId;
    this.allowCustomOrderFlow = allowCustomOrderFlow;
    this.allowProductListOrderFlow = allowProductListOrderFlow;
    this.language = language;
    this.productListFlowTitle = productListFlowTitle;
    this.productListFlowDescription = productListFlowDescription;
    this.productListFlowConfirmationTitle = productListFlowConfirmationTitle;
    this.productListFlowConfirmationDescription = productListFlowConfirmationDescription;
    this.productListFlowConfirmationNextSteps = productListFlowConfirmationNextSteps;
    this.customOrderFlowTitle = customOrderFlowTitle;
    this.customOrderFlowDescription = customOrderFlowDescription;
    this.customOrderFlowConfirmationTitle = customOrderFlowConfirmationTitle;
    this.customOrderFlowConfirmationDescription = customOrderFlowConfirmationDescription;
    this.customOrderFlowConfirmationNextSteps = customOrderFlowConfirmationNextSteps;
    this.primaryColor = primaryColor;
    this.primaryColorLight = primaryColorLight;
    this.primaryColorDark = primaryColorDark;
    this.headerColor = headerColor;
    this.logoUrl = logoUrl;
    this.logoRedirectUrl = logoRedirectUrl;
    this.defaultPickupLocation = defaultPickupLocation;
    this.savedUrl = savedUrl;
    this.urlSlug = urlSlug;
    this.useCustomUrlSlug = useCustomUrlSlug;
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.showPricesWithVat = showPricesWithVat;
    this.enableCategoryFilter = enableCategoryFilter;
    this.categoryFilters = categoryFilters;
    this.productOrder = productOrder;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;

  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  setData(updates: Partial<IPublicFormConfig>) {
    return Object.assign(this, updates);
  }

  data() {
    const result: any = {
      id: this.id || "",
      workspaceId: this.workspaceId,
      allowCustomOrderFlow: this.allowCustomOrderFlow,
      allowProductListOrderFlow: this.allowProductListOrderFlow,
      language: this.language || 'no',
      productListFlowTitle: this.productListFlowTitle,
      productListFlowDescription: this.productListFlowDescription,
      productListFlowConfirmationTitle: this.productListFlowConfirmationTitle,
      productListFlowConfirmationDescription: this.productListFlowConfirmationDescription,
      productListFlowConfirmationNextSteps: this.productListFlowConfirmationNextSteps,
      customOrderFlowTitle: this.customOrderFlowTitle,
      customOrderFlowDescription: this.customOrderFlowDescription,
      customOrderFlowConfirmationTitle: this.customOrderFlowConfirmationTitle,
      customOrderFlowConfirmationDescription: this.customOrderFlowConfirmationDescription,
      customOrderFlowConfirmationNextSteps: this.customOrderFlowConfirmationNextSteps,
      logoUrl: this.logoUrl || "",
      logoRedirectUrl: this.logoRedirectUrl || "",
      savedUrl: this.savedUrl || "",
      urlSlug: this.urlSlug || "",
      useCustomUrlSlug: this.useCustomUrlSlug || false,
      clientId: this.clientId || "",
      clientSecret: this.clientSecret || "",
      showPricesWithVat: this.showPricesWithVat ?? true,
      enableCategoryFilter: this.enableCategoryFilter ?? false,
      categoryFilters: this.categoryFilters || [],
      productOrder: this.productOrder || [],
      createdAt: this.createdAt || null,
      updatedAt: this.updatedAt || null,
    };

    // Only include optional color fields if they have values
    if (this.primaryColor) result.primaryColor = this.primaryColor;
    if (this.primaryColorLight) result.primaryColorLight = this.primaryColorLight;
    if (this.primaryColorDark) result.primaryColorDark = this.primaryColorDark;
    if (this.headerColor) result.headerColor = this.headerColor;
    if (this.defaultPickupLocation) {
      // Ensure plain object, not class instance
      const locAny: any = this.defaultPickupLocation as any;
      result.defaultPickupLocation = typeof locAny.data === 'function' ? locAny.data() : { ...this.defaultPickupLocation };
    }

    // Do not include undefined fields; Firestore rejects undefined values
    return result;
  }

  static isSlugTaken = async (slug: string, currentConfigId: string) => {
    if (!slug) return false; // Don't check empty slugs
    const q = query(
      firestore
        .collection(PublicFormConfig.collectionName)
        .where("urlSlug", "==", slug)
        .limit(2) // Limit to 2 to see if there's more than one
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return false; // Slug does not exist
    }

    // If the slug exists, check if it belongs to the current config
    const conflictingDocs = snapshot.docs.filter(doc => doc.id !== currentConfigId);
    
    return conflictingDocs.length > 0; // If there are other docs with this slug, it's taken
  };

  static getByUrlSlug = async (slug: string) => {
    // This is a placeholder for the actual API call.
    // In a real scenario, this would be an API endpoint that queries the backend.
    // For now, we'll query Firestore directly. This is not secure for production
    // if the collection rules are not set up correctly.
    const configs = await getDocs(
      query(
        firestore
          .collection(PublicFormConfig.collectionName)
          .where("urlSlug", "==", slug)
          .limit(1)
          .withConverter(PublicFormConfig.converter)
      )
    );
    
    if (!configs.empty) {
      const config = configs.docs[0].data();
      return config;
    }
    return null;
  };

  static list = (workspaceId: string) => {
    return query(
      firestore
        .collection(PublicFormConfig.collectionName)
        .where("workspaceId", "==", workspaceId)
        .withConverter(PublicFormConfig.converter),
    );
  };

  static get = (id: string) => {
    return doc(
      firestore.collection(PublicFormConfig.collectionName).withConverter(PublicFormConfig.converter),
      id,
    );
  };

  static getByWorkspaceId = async (workspaceId: string) => {
    const configs = await getDocs(
      query(
        firestore
          .collection(PublicFormConfig.collectionName)
          .where("workspaceId", "==", workspaceId)
          .withConverter(PublicFormConfig.converter)
      )
    );
    
    if (!configs.empty) {
      // The converter should have already converted the data properly
      const config = configs.docs[0].data();
      // If it's a PublicFormConfig instance, call data() to get the plain object
      if (config && typeof config === 'object' && 'data' in config) {
        return config.data();
      }
      
      return config;
    }
    return null;
  };

  static create = async (workspaceId: string, data: Omit<IPublicFormConfig, "id" | "createdAt" | "updatedAt">) => {
    const now = Timestamp.now();
    const newConfig = new PublicFormConfig({
      ...data,
      workspaceId: workspaceId,
      id: PublicFormConfig.createId(),
      createdAt: now,
      updatedAt: now,
    });
    try {
      await firestore
        .collection(PublicFormConfig.collectionName)
        .doc(newConfig.id)
        .withConverter(PublicFormConfig.converter)
        .set(newConfig, { merge: true });
    } catch (e) {
      console.warn("Create public form config failed with error: ", e);
      return false;
    }
    return true;
  };

  static update = async (
    config: PublicFormConfig,
    updates: Partial<IPublicFormConfig>
  ) => {
    const updateConfig = config.clone();

    updateConfig.setData({
      ...updates,
      updatedAt: Timestamp.now(),
    });



    await firestore
      .collection(PublicFormConfig.collectionName)
      .doc(config.id)
      .withConverter(PublicFormConfig.converter)
      .set(updateConfig, { merge: true });

    return updateConfig;
  };

  static remove = async (config: PublicFormConfig) => {
    return await firestore
      .collection(PublicFormConfig.collectionName)
      .doc(config.id)
      .delete();
  };
}

export default PublicFormConfig; 
